import { useEffect, useState } from "react";
import AppShell from "@/components/layout/AppShell";
import api, { fmtError } from "@/lib/api";
import { DataTable, Badge, Btn } from "@/components/ui-lite";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Wallet, Receipt, ArrowRight, AlertTriangle, RefreshCw, Info, ExternalLink } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

function defaultMonth() {
  const d = new Date(); d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}
const fmt = (v, digits = 2) => v == null ? "—" : Number(v).toLocaleString("en-US", { minimumFractionDigits: digits, maximumFractionDigits: digits });
const fmtUsd = (v) => `$${fmt(v)}`;

function Kpi({ label, value, tone = "default", sub }) {
  const toneCls = tone === "warning" ? "text-[var(--wv-warning)]"
    : tone === "error" ? "text-[var(--wv-error)]"
    : tone === "success" ? "text-[var(--wv-success)]"
    : "text-[var(--wv-text)]";
  return (
    <div className="wv-card p-5">
      <div className="wv-overline">{label}</div>
      <div className={`mt-2 text-2xl font-medium tracking-tight ${toneCls}`} style={{ fontFamily: "Outfit" }}>{value}</div>
      {sub && <div className="text-xs text-[var(--wv-text-3)] mt-1">{sub}</div>}
    </div>
  );
}

function NoteCell({ month, payeeCode, initial }) {
  const [val, setVal] = useState(initial || "");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (val === (initial || "")) return;
    setSaving(true);
    try {
      await api.put(`/monthly-summary/${month}/notes`, { payee_code: payeeCode, note: val });
      toast.success("Note saved");
    } catch { toast.error("Failed to save note"); }
    finally { setSaving(false); }
  };
  return (
    <textarea
      data-testid={`mo-note-${payeeCode}`}
      value={val}
      onChange={(e) => setVal(e.target.value)}
      onBlur={save}
      placeholder="Why is diff off?"
      rows={1}
      className={`w-56 text-xs px-2 py-1 rounded bg-white/70 border border-[var(--wv-border)] focus:outline-none focus:ring-1 focus:ring-[var(--wv-primary)] resize-y ${saving ? "opacity-60" : ""}`}
    />
  );
}

function InfoPill({ label, count, testid, children }) {
  if (!count) return <span className="text-[var(--wv-text-3)] text-xs">—</span>;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid={testid}
          className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white/70 border border-[var(--wv-border)] text-xs font-mono hover:bg-white hover:border-[var(--wv-primary)]/40 transition-colors"
          title={`${count} ${label}`}
        >
          <span className="tabular-nums">{count}</span>
          <Info size={11} className="text-[var(--wv-text-2)]"/>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 max-h-72 overflow-auto text-xs bg-white/95 backdrop-blur-md p-3">
        <div className="wv-overline mb-2">{label}</div>
        {children}
      </PopoverContent>
    </Popover>
  );
}

export default function MonthlyOverviewPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [month, setMonth] = useState(defaultMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [expandedBrand, setExpandedBrand] = useState(null);

  const fetchSummary = () => {
    setLoading(true);
    return api.get(`/monthly-summary/${month}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(fmtError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { fetchSummary(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [month]);

  const refreshOutstanding = async () => {
    if (!window.confirm(`Refresh Outstanding for ${month}?\n\nThis re-applies each provider's current default_ggr_pct to the locked snapshot rows. The prior snapshot is archived to history (audit trail). FX rates are untouched.\n\nUse this if you seeded/edited providers after running the GGR snapshot.`)) return;
    setRefreshing(true);
    try {
      const { data } = await api.post(`/monthly-summary/${month}/refresh-outstanding`);
      toast.success(`Refreshed v${data.version} · ${data.rows_updated} rows updated${data.rows_skipped_unmapped ? ` · ${data.rows_skipped_unmapped} skipped (unmapped)` : ""}`, { duration: 6000 });
      fetchSummary();
    } catch (e) { toast.error(fmtError(e)); }
    finally { setRefreshing(false); }
  };

  const k = data?.kpi;
  const providers = data?.providers || [];
  const rawPayees = data?.payees || [];

  // Row-level state helpers
  const CARRIED = "carried_forward";
  const isCarriedOnly = (r) => {
    const statuses = r.invoice_statuses || [];
    return statuses.length > 0 && statuses.every((s) => s === CARRIED || s === "paid" || s === "verified") && statuses.includes(CARRIED);
  };
  const isFullySettled = (r) => {
    const statuses = r.invoice_statuses || [];
    if (statuses.length === 0) return false;
    return statuses.every((s) => s === "paid" || s === "verified");
  };
  const isUnpaid = (r) => (r.invoice_statuses || []).some((s) => !s || s === "pending");

  // Sort: unpaid → mixed → carried_forward → fully settled. Within each bucket keep GGR-outstanding desc.
  const bucket = (r) => {
    if (isUnpaid(r)) return 0;
    if (isFullySettled(r)) return 3;
    if (isCarriedOnly(r)) return 2;
    return 1; // mixed
  };
  const payees = [...rawPayees].sort((a, b) => {
    const bd = bucket(a) - bucket(b);
    if (bd !== 0) return bd;
    return (b.outstanding_by_ggr_usd || 0) - (a.outstanding_by_ggr_usd || 0);
  });

  const brandPayables = data?.brand_payables_from_ggr || [];
  const brands = data?.brand_breakdown || [];
  const diag = data?.diagnostics || {};
  const hasDiag = (diag.unmapped_providers?.length || 0) + (diag.missing_fx_currencies?.length || 0) + (diag.zero_ggr_pct_providers?.length || 0) > 0;

  const statusTone = (s) => s === "verified" ? "success" : s === "paid" ? "info" : s === "carried_forward" ? "warning" : s === "pending" ? "warning" : "default";
  const statusLabel = (s) => s === "carried_forward" ? "carried fwd" : s;
  const diffTone = (v) => Math.abs(v || 0) < 0.01 ? "success" : (v > 0 ? "warning" : "error");

  return (
    <AppShell
      title="Monthly Overview"
      subtitle="Vendor outstanding vs invoiced, brand-wise breakdown, per month"
      actions={
        <div className="flex gap-2 items-center">
          <input data-testid="mo-month" type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10 px-3 rounded-sm bg-white border border-[var(--wv-border)] font-mono text-sm"/>
          {isAdmin && data?.has_snapshot && (
            <Btn variant="secondary" data-testid="mo-refresh-outstanding" onClick={refreshOutstanding} disabled={refreshing}>
              <RefreshCw size={14} className="inline mr-1"/>{refreshing ? "Refreshing..." : "Refresh Outstanding"}
            </Btn>
          )}
          <Link to="/monthly-run" className="text-sm text-[var(--wv-primary)] hover:underline flex items-center gap-1">
            Monthly GGR Run <ArrowRight size={14}/>
          </Link>
        </div>
      }
    >
      {!data && loading && <div className="text-sm text-[var(--wv-text-3)]">Loading…</div>}

      {data && !data.has_snapshot && (
        <div className="wv-card p-5 mb-6 flex items-start gap-3" data-testid="mo-no-snapshot">
          <div className="mt-0.5 h-9 w-9 rounded-xl bg-[var(--wv-warning)]/15 flex items-center justify-center text-[var(--wv-warning)]"><AlertTriangle size={16}/></div>
          <div>
            <div className="font-medium text-[var(--wv-text)]">No GGR snapshot for {month} yet</div>
            <div className="text-sm text-[var(--wv-text-2)] mt-0.5">
              The "outstanding by GGR" figure will be $0 until you run the monthly GGR aggregation.
              <Link to="/monthly-run" className="ml-1 text-[var(--wv-primary)] hover:underline">Run it now →</Link>
            </div>
          </div>
        </div>
      )}

      {k && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-testid="mo-kpi-row">
          <Kpi label="Outstanding by GGR" value={fmtUsd(k.outstanding_by_ggr_usd)} sub="Our computed provider share (USD)"/>
          <Kpi label="Invoiced by Vendors" value={fmtUsd(k.invoiced_usd)} sub={`${k.invoice_count} invoices`}/>
          <Kpi label="Difference (Invoice − GGR)" value={`${k.diff_usd >= 0 ? "+" : ""}${fmtUsd(k.diff_usd)}`} tone={diffTone(k.diff_usd)} sub="Positive = vendors invoicing more than our GGR"/>
          <Kpi label="Paid / Pending" value={`${fmtUsd(k.paid_usd)} / ${fmtUsd(k.unpaid_usd)}`} sub={`${k.paid_count} paid · ${k.pending_count} pending · ${k.verified_count} verified${k.carried_forward_count ? ` · ${k.carried_forward_count} carried fwd` : ""}`}/>
        </div>
      )}

      {k && k.carried_forward_count > 0 && (
        <div className="wv-card p-4 mb-6 border border-[var(--wv-warning)]/30 bg-[var(--wv-warning)]/5 flex items-center gap-3" data-testid="mo-carried-fwd-note">
          <div className="h-9 w-9 rounded-xl bg-[var(--wv-warning)]/15 flex items-center justify-center text-[var(--wv-warning)]"><RefreshCw size={16}/></div>
          <div className="text-sm">
            <div className="font-medium">{k.carried_forward_count} invoice{k.carried_forward_count === 1 ? "" : "s"} carried forward · {fmtUsd(k.carried_forward_usd)}</div>
            <div className="text-xs text-[var(--wv-text-2)]">Deferred to a future month by ops. They don't count against this month's pending total and are shown in amber below.</div>
          </div>
        </div>
      )}

      {k && hasDiag && (
        <div className="wv-card p-5 mb-6 border border-[var(--wv-warning)]/30" data-testid="mo-diagnostics">
          <div className="flex items-start gap-3">
            <div className="mt-0.5 h-9 w-9 rounded-xl bg-[var(--wv-warning)]/15 flex items-center justify-center text-[var(--wv-warning)]">
              <AlertTriangle size={16}/>
            </div>
            <div className="flex-1">
              <div className="font-medium text-[var(--wv-text)]">Why some Outstanding numbers may be off</div>
              <div className="text-sm text-[var(--wv-text-2)] mt-2 space-y-1.5">
                {diag.unmapped_providers?.length > 0 && (
                  <div>
                    <span className="font-medium">Providers in GGR data but not in your Providers list ({diag.unmapped_providers.length}):</span>{" "}
                    <span className="font-mono text-xs">{diag.unmapped_providers.slice(0, 20).join(", ")}{diag.unmapped_providers.length > 20 ? "…" : ""}</span>
                    <div className="text-xs text-[var(--wv-text-3)] mt-0.5">Add these under Settings → Providers, then click <strong>Refresh Outstanding</strong> above.</div>
                  </div>
                )}
                {diag.missing_fx_currencies?.length > 0 && (
                  <div>
                    <span className="font-medium">Missing FX rate for {month}:</span>{" "}
                    <span className="font-mono text-xs">{diag.missing_fx_currencies.join(", ")}</span>
                    <div className="text-xs text-[var(--wv-text-3)] mt-0.5">Add these under Settings → FX Rates (per-month). Numbers will pick up the new rate automatically — no refresh needed.</div>
                  </div>
                )}
                {diag.zero_ggr_pct_providers?.length > 0 && (
                  <div>
                    <span className="font-medium">Providers with GGR % = 0 ({diag.zero_ggr_pct_providers.length}):</span>{" "}
                    <span className="font-mono text-xs">{diag.zero_ggr_pct_providers.slice(0, 20).join(", ")}{diag.zero_ggr_pct_providers.length > 20 ? "…" : ""}</span>
                    <div className="text-xs text-[var(--wv-text-3)] mt-0.5">Set their <code>default_ggr_pct</code> under Settings → Providers, then click <strong>Refresh Outstanding</strong> above.</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {k && (
        <div className="wv-card p-6 mb-6" data-testid="mo-payees-card">
          <div className="flex items-center gap-2 mb-3">
            <Wallet size={16} className="text-[var(--wv-text-2)]"/>
            <div className="wv-overline">Payables by payee · who you actually pay</div>
          </div>
          <DataTable
            testid="mo-payees-table"
            columns={[
              { key: "payee_code", label: "Payee", render: (r) => (
                <div>
                  <div className="font-medium">{r.payee_name || r.payee_code}</div>
                  <div className="text-xs text-[var(--wv-text-3)] font-mono">{r.payee_code} {r.is_aggregator ? "· aggregator" : ""}</div>
                </div>
              ) },
              { key: "provider_codes", label: "Rolls up", render: (r) => (
                <InfoPill
                  label={`providers rolled into ${r.payee_code}`}
                  count={(r.provider_codes || []).length}
                  testid={`mo-rollup-${r.payee_code}`}
                >
                  <div className="flex flex-wrap gap-1">
                    {(r.provider_codes || []).map((c) => (
                      <span key={c} className="px-1.5 py-0.5 rounded bg-[var(--wv-surface-alt)] border border-[var(--wv-border)] font-mono">{c}</span>
                    ))}
                  </div>
                </InfoPill>
              ) },
              { key: "invoice_currency", label: "Cur", mono: true, render: (r) => <Badge tone={r.invoice_currency === "USD" ? "success" : "info"}>{r.invoice_currency}</Badge> },
              { key: "outstanding_local", label: "Payable (local)", mono: true, render: (r) => r.outstanding_by_ggr_local != null ? `${fmt(r.outstanding_by_ggr_local)} ${r.invoice_currency}` : <span className="text-[var(--wv-warning)] text-xs">no FX</span> },
              { key: "outstanding_by_ggr_usd", label: "Payable (USD)", mono: true, render: (r) => fmtUsd(r.outstanding_by_ggr_usd) },
              { key: "invoiced_usd", label: "Invoiced (USD)", mono: true, render: (r) => fmtUsd(r.invoiced_usd) },
              { key: "diff_usd", label: "Diff", mono: true, render: (r) => <span className={`font-medium ${Math.abs(r.diff_usd) < 0.01 ? "text-[var(--wv-success)]" : r.diff_usd > 0 ? "text-[var(--wv-warning)]" : "text-[var(--wv-error)]"}`}>{r.diff_usd >= 0 ? "+" : ""}{fmt(r.diff_usd)}</span> },
              { key: "note", label: "Diff notes", render: (r) => <NoteCell month={month} payeeCode={r.payee_code} initial={r.note || ""}/> },
              { key: "invoice_count", label: "Inv", mono: true },
              { key: "invoice_statuses", label: "Status", render: (r) => {
                const statuses = r.invoice_statuses || [];
                const counts = statuses.reduce((acc, s) => { acc[s] = (acc[s] || 0) + 1; return acc; }, {});
                const entries = Object.entries(counts);
                if (entries.length === 0) return <span className="text-[var(--wv-text-3)] text-xs">—</span>;
                const invsAll = r.invoices || [];
                return (
                  <div className="flex flex-wrap gap-1">
                    {entries.map(([s, n]) => (
                      <Popover key={s}>
                        <PopoverTrigger asChild>
                          <button
                            data-testid={`mo-payee-status-${r.payee_code}-${s}`}
                            className="cursor-pointer focus:outline-none"
                            title="Click to see the individual invoices"
                          >
                            <Badge tone={statusTone(s)}>{statusLabel(s)}{n > 1 ? ` ×${n}` : ""}</Badge>
                          </button>
                        </PopoverTrigger>
                        <PopoverContent align="start" className="w-80 max-h-80 overflow-auto p-3 bg-white/95 backdrop-blur-md">
                          <div className="wv-overline mb-2">{statusLabel(s)} invoices · {r.payee_name || r.payee_code}</div>
                          <div className="flex flex-col gap-1">
                            {invsAll.filter((i) => i.status === s).map((inv) => (
                              <Link
                                key={inv.id}
                                to={`/invoices?month=${month}&highlight=${encodeURIComponent(inv.invoice_number)}`}
                                data-testid={`mo-invoice-link-${inv.invoice_number}`}
                                className="flex items-center justify-between gap-2 px-2 py-1.5 rounded hover:bg-[var(--wv-accent-light)]/60 group text-xs"
                              >
                                <div className="min-w-0">
                                  <div className="font-mono font-medium truncate">{inv.invoice_number || "(no #)"}</div>
                                  <div className="font-mono text-[10px] text-[var(--wv-text-3)]">{inv.provider_code}</div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                  <span className="font-mono">{fmtUsd(inv.total_amount_usd)}</span>
                                  <ExternalLink size={12} className="text-[var(--wv-text-3)] group-hover:text-[var(--wv-primary)]"/>
                                </div>
                              </Link>
                            ))}
                            {invsAll.filter((i) => i.status === s).length === 0 && (
                              <div className="text-xs text-[var(--wv-text-3)]">No invoice details available.</div>
                            )}
                          </div>
                        </PopoverContent>
                      </Popover>
                    ))}
                  </div>
                );
              } },
            ]}
            rows={payees}
            rowClassName={(r) =>
              isFullySettled(r)
                ? "!bg-[var(--wv-success)]/10 hover:!bg-[var(--wv-success)]/15"
                : isCarriedOnly(r)
                  ? "!bg-[var(--wv-warning)]/10 hover:!bg-[var(--wv-warning)]/15"
                  : ""
            }
          />
        </div>
      )}

      {k && brandPayables.length > 0 && (
        <div className="wv-card p-6 mb-6" data-testid="mo-brand-payables-card">
          <div className="flex items-center gap-2 mb-3">
            <Receipt size={16} className="text-[var(--wv-text-2)]"/>
            <div className="wv-overline">Payables by brand · from GGR snapshot</div>
          </div>
          <div className="text-xs text-[var(--wv-text-3)] mb-3">
            For each brand this month, sum of provider_share (in brand's local currency) across all providers active on that brand, converted to USD via the brand's FX rate. Click any row to see per-provider contribution.
          </div>
          <DataTable
            testid="mo-brand-payables-table"
            columns={[
              { key: "brand_code", label: "Brand", render: (r) => (
                <div>
                  <div className="font-medium">{r.brand_name || r.brand_code}</div>
                  <div className="text-xs text-[var(--wv-text-3)] font-mono">{r.brand_code} · id {r.brand_id}</div>
                </div>
              ) },
              { key: "brand_currency", label: "Currency", mono: true },
              { key: "provider_count", label: "Providers", mono: true },
              { key: "orders", label: "Orders", mono: true, render: (r) => fmt(r.orders, 0) },
              { key: "turnover_local", label: "Turnover (local)", mono: true, render: (r) => fmt(r.turnover_local) },
              { key: "total_local", label: "Payable (local)", mono: true, render: (r) => <span className="font-medium">{fmt(r.total_local)}</span> },
              { key: "total_usd", label: "Payable (USD)", mono: true, render: (r) => <span className="font-medium">{fmtUsd(r.total_usd)}</span> },
            ]}
            rows={brandPayables.map((b, i) => ({ ...b, id: b.brand_id || `brand-${i}` }))}
            expandable={(r) => {
              const bps = r.by_provider || [];
              if (bps.length === 0) return <div className="text-sm text-[var(--wv-text-3)]">No providers contributed to this brand.</div>;
              const totalUsd = r.total_usd || 1;
              return (
                <div>
                  <div className="wv-overline mb-2">Provider contribution to {r.brand_name || r.brand_code}</div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-[var(--wv-text-3)] text-xs uppercase tracking-widest">
                        <th className="text-left py-1 pr-3">Provider</th>
                        <th className="text-left py-1 pr-3">Paid to</th>
                        <th className="text-right py-1 pr-3">Orders</th>
                        <th className="text-right py-1 pr-3">Turnover ({r.brand_currency})</th>
                        <th className="text-right py-1 pr-3">Share ({r.brand_currency})</th>
                        <th className="text-right py-1 pr-3">Share (USD)</th>
                        <th className="text-right py-1">% of brand</th>
                      </tr>
                    </thead>
                    <tbody>
                      {bps.map((p, idx) => (
                        <tr key={idx} className="border-t border-white/50">
                          <td className="py-1.5 pr-3 font-mono">{p.provider_code}</td>
                          <td className="py-1.5 pr-3 text-xs"><span className="font-mono">{p.payee_code}</span>{p.is_aggregator ? <Badge tone="info" className="ml-1">agg</Badge> : null}</td>
                          <td className="py-1.5 pr-3 text-right font-mono">{fmt(p.orders, 0)}</td>
                          <td className="py-1.5 pr-3 text-right font-mono">{fmt(p.turnover_local)}</td>
                          <td className="py-1.5 pr-3 text-right font-mono">{fmt(p.share_local)}</td>
                          <td className="py-1.5 pr-3 text-right font-mono font-medium">{fmtUsd(p.share_usd)}</td>
                          <td className="py-1.5 text-right font-mono">{((p.share_usd / totalUsd) * 100).toFixed(1)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            }}
            expandedRow={expandedBrand}
            onToggleExpand={(id) => setExpandedBrand((cur) => cur === id ? null : id)}
          />
        </div>
      )}

      {k && (
        <div className="wv-card p-6">
          <div className="flex items-center gap-2 mb-3">
            <Receipt size={16} className="text-[var(--wv-text-2)]"/>
            <div className="wv-overline">Brand-wise breakdown from invoices</div>
          </div>
          <DataTable
            testid="mo-brands-table"
            columns={[
              { key: "invoice_number", label: "Invoice #", mono: true },
              { key: "provider_code", label: "Provider", mono: true },
              { key: "brand_name", label: "Brand" },
              { key: "amount", label: "Amount", mono: true, render: (r) => `${fmt(r.amount)} ${r.currency}` },
              { key: "amount_usd", label: "USD", mono: true, render: (r) => fmtUsd(r.amount_usd) },
              { key: "share_pct", label: "Share %", mono: true, render: (r) => r.share_pct != null ? `${Number(r.share_pct).toFixed(1)}%` : "—" },
              { key: "management_fee_usd", label: "Mgmt Fee", mono: true, render: (r) => r.management_fee_usd ? fmtUsd(r.management_fee_usd) : "—" },
              { key: "invoice_status", label: "Status", render: (r) => (
                <Link
                  to={`/invoices?month=${month}&highlight=${encodeURIComponent(r.invoice_number || "")}`}
                  data-testid={`mo-brand-invoice-link-${r.invoice_number || r.provider_code}`}
                  title="Open in Invoices"
                  className="inline-flex items-center gap-1 group"
                >
                  <Badge tone={statusTone(r.invoice_status)}>{statusLabel(r.invoice_status)}</Badge>
                  <ExternalLink size={11} className="text-[var(--wv-text-3)] group-hover:text-[var(--wv-primary)]"/>
                </Link>
              ) },
            ]}
            // Sort: pending → carried_forward → paid → verified. Within a bucket keep highest USD first.
            rows={[...brands].sort((a, b) => {
              const rank = (s) => s === "verified" ? 3 : s === "paid" ? 2 : s === "carried_forward" ? 1 : 0;
              const rd = rank(a.invoice_status) - rank(b.invoice_status);
              if (rd !== 0) return rd;
              return (b.amount_usd || 0) - (a.amount_usd || 0);
            })}
            rowClassName={(r) =>
              (r.invoice_status === "paid" || r.invoice_status === "verified")
                ? "!bg-[var(--wv-success)]/10 hover:!bg-[var(--wv-success)]/15"
                : r.invoice_status === "carried_forward"
                  ? "!bg-[var(--wv-warning)]/10 hover:!bg-[var(--wv-warning)]/15"
                  : ""
            }
          />
          {brands.length === 0 && k.invoice_count > 0 && (
            <div className="text-sm text-[var(--wv-text-3)] mt-3">Invoices exist but none have brand allocations yet. Open an invoice and click "Auto-allocate by winloss %" to populate this table.</div>
          )}
        </div>
      )}
    </AppShell>
  );
}
